function normalizeSidebarPath(value) {
  const path = String(value || "").split(/[?#]/, 1)[0] || "/";
  return path === "/" ? path : path.replace(/\/$/, "");
}

function sidebarPathMatches(pathname, target, exact = false) {
  const currentPath = normalizeSidebarPath(pathname);
  const targetPath = normalizeSidebarPath(target);
  if (exact) return currentPath === targetPath;
  return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
}

/** Match sidebar sub-item URL or optional `activePathPrefixes` against the current path. */
export function sidebarSubItemMatchesPath(pathname, subItem) {
  if (Array.isArray(subItem.activePathPrefixes) && subItem.activePathPrefixes.length > 0) {
    return subItem.activePathPrefixes.some((prefix) => sidebarPathMatches(pathname, prefix));
  }
  const { url, exact, excludePathPrefixes } = subItem;
  if (!url || url === "#") return false;

  if (Array.isArray(excludePathPrefixes) && excludePathPrefixes.some((prefix) => sidebarPathMatches(pathname, prefix))) {
    return false;
  }

  return sidebarPathMatches(pathname, url, exact);
}

/** Match the current query string against an item's explicit active parameters. */
export function sidebarSearchParamsMatch(search, activeSearchParams, allowMissing = false) {
  if (!activeSearchParams || typeof activeSearchParams !== "object") return true;

  const params = search instanceof URLSearchParams
    ? search
    : new URLSearchParams(search || "");

  return Object.entries(activeSearchParams).every(([name, expected]) => {
    const actual = params.get(name);
    if (actual === null && allowMissing) return true;
    if (Array.isArray(expected)) return expected.some((value) => String(value) === actual);
    return actual === String(expected);
  });
}

/** Normalize title/url/id tokens so "survey-filling" matches "survey filling" / "surveyfilling". */
export function normalizeSidebarSearchText(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[-_/]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function sidebarPageMatchesQuery(page, query) {
  const q = normalizeSidebarSearchText(query);
  if (!q) return true;

  const qCompact = q.replace(/\s+/g, "");
  const haystacks = [
    page.title,
    page.id,
    page.url,
    page.searchText,
    page.parent,
    page.grandParent,
  ]
    .filter(Boolean)
    .map(normalizeSidebarSearchText);

  return haystacks.some((hay) => {
    if (hay.includes(q)) return true;
    const hayCompact = hay.replace(/\s+/g, "");
    return Boolean(qCompact) && hayCompact.includes(qCompact);
  });
}

/** Group flattened destinations under their top-level sidebar tab. */
export function groupSidebarPagesForWorkspace(pages) {
  const groups = [];
  const groupByTitle = new Map();

  for (const page of pages || []) {
    const title = page.type === "hub-tab"
      ? page.grandParent || page.parent
      : page.parent;
    if (!title) {
      groups.push({ id: `page:${page.url}`, title: null, icon: page.icon, pages: [page] });
      continue;
    }

    let group = groupByTitle.get(title);
    if (!group) {
      group = { id: `group:${title}`, title, icon: page.icon, pages: [] };
      groupByTitle.set(title, group);
      groups.push(group);
    }
    group.pages.push(page);
  }

  return groups;
}

/** Select a whole page group, or clear it when every page is already selected. */
export function toggleSidebarPageSelection(selectedUrls, pageUrls) {
  const selected = new Set(Array.isArray(selectedUrls) ? selectedUrls : []);
  const targets = [...new Set(Array.isArray(pageUrls) ? pageUrls : [])];
  const shouldClear = targets.length > 0 && targets.every((url) => selected.has(url));
  for (const url of targets) {
    if (shouldClear) selected.delete(url);
    else selected.add(url);
  }
  return [...selected];
}

/**
 * @param {string} query
 * @param {{ navMain: Array, navSecondary?: Array }} data
 * @param {Array} [extraPages] Hub-tab pages (e.g. Surveys) not listed as sidebar leaves.
 */
export function searchSidebarPages(query, data, extraPages = []) {
  const allPages = [];

  data.navMain.forEach((item) => {
    if (item.url !== "#") {
      allPages.push({
        id: item.id,
        title: item.title,
        url: item.url,
        icon: item.icon,
        type: "main",
        exact: item.exact,
        activePathPrefixes: item.activePathPrefixes,
        excludePathPrefixes: item.excludePathPrefixes,
        searchText: [item.id, item.url].filter(Boolean).join(" "),
      });
    }

    if (item.items) {
      item.items.forEach((subItem) => {
        if (subItem.url && subItem.url !== "#") {
          allPages.push({
            id: subItem.id,
            title: subItem.title,
            url: subItem.url,
            icon: item.icon,
            type: "sub",
            parent: item.title,
            exact: subItem.exact,
            activePathPrefixes: subItem.activePathPrefixes,
            excludePathPrefixes: subItem.excludePathPrefixes,
            searchText: [subItem.id, subItem.url].filter(Boolean).join(" "),
          });
        }

        if (subItem.items) {
          subItem.items.forEach((subSubItem) => {
            if (!subSubItem.url || subSubItem.url === "#") return;
            allPages.push({
              id: subSubItem.id,
              title: subSubItem.title,
              url: subSubItem.url,
              icon: item.icon,
              type: "sub-sub",
              parent: item.title,
              grandParent: subItem.title,
              exact: subSubItem.exact,
              activePathPrefixes: subSubItem.activePathPrefixes,
              excludePathPrefixes: subSubItem.excludePathPrefixes,
              searchText: [subSubItem.id, subSubItem.url].filter(Boolean).join(" "),
            });
          });
        }
      });
    }
  });

  (data.navSecondary || []).forEach((item) => {
    if (!item.url || item.url === "#") return;
    allPages.push({
      id: item.id,
      title: item.title,
      url: item.url,
      icon: item.icon,
      type: "secondary",
      exact: item.exact,
      activePathPrefixes: item.activePathPrefixes,
      excludePathPrefixes: item.excludePathPrefixes,
      searchText: [item.id, item.url].filter(Boolean).join(" "),
    });
  });

  for (const page of extraPages) {
    if (!page?.url || page.url === "#") continue;
    allPages.push(page);
  }

  if (!query) return allPages;

  return allPages.filter((page) => sidebarPageMatchesQuery(page, query));
}

function sidebarItemMatchesModule(item, moduleKey, resolveModules) {
  const explicitModules = Array.isArray(item?.focusModules) ? item.focusModules : [];
  const resolved = resolveModules?.(item);
  const resolvedModules = Array.isArray(resolved) ? resolved : resolved ? [resolved] : [];
  return explicitModules.includes(moduleKey) || resolvedModules.includes(moduleKey);
}

function filterSidebarItemByModule(item, moduleKey, resolveModules) {
  if (item.section === "quickAccess") return item;

  const explicitBranchMatch = Array.isArray(item.focusModules)
    && item.focusModules.includes(moduleKey)
    && Array.isArray(item.items);
  if (explicitBranchMatch) return item;

  const items = Array.isArray(item.items)
    ? item.items
      .map((child) => filterSidebarItemByModule(child, moduleKey, resolveModules))
      .filter(Boolean)
    : null;

  if (!sidebarItemMatchesModule(item, moduleKey, resolveModules) && !items?.length) {
    return null;
  }
  return items ? { ...item, items } : item;
}

/** Narrow already-authorized navigation to a module workspace without changing access. */
export function filterSidebarNavByModule(navData, moduleKey, resolveModules) {
  if (!moduleKey || moduleKey === "all") return navData;

  return {
    ...navData,
    navMain: (navData.navMain || [])
      .map((item) => filterSidebarItemByModule(item, moduleKey, resolveModules))
      .filter(Boolean),
    navSecondary: (navData.navSecondary || [])
      .map((item) => filterSidebarItemByModule(item, moduleKey, resolveModules))
      .filter(Boolean),
  };
}

/** Filter authorized hub-search destinations with the same module semantics as the sidebar. */
export function filterSidebarPagesByModule(pages, moduleKey, resolveModules) {
  if (!moduleKey || moduleKey === "all") return pages;
  return (pages || []).filter((page) => sidebarItemMatchesModule(page, moduleKey, resolveModules));
}

function sidebarSelectedUrlMatchesItem(selectedUrl, item) {
  if (!sidebarSubItemMatchesPath(selectedUrl, item)) return false;

  const searchStart = String(selectedUrl || "").indexOf("?");
  if (searchStart === -1 || !item.activeSearchParams) return true;

  const search = String(selectedUrl || "").slice(searchStart + 1).split("#", 1)[0];
  return sidebarSearchParamsMatch(search, item.activeSearchParams);
}

function filterSidebarItemByUrls(item, pageUrls) {
  if (item.section === "quickAccess") return item;

  const items = Array.isArray(item.items)
    ? item.items
      .map((child) => filterSidebarItemByUrls(child, pageUrls))
      .filter(Boolean)
    : null;
  const ownMatch = pageUrls.some((url) => sidebarSelectedUrlMatchesItem(url, item));

  if (!ownMatch && !items?.length) return null;
  return items ? { ...item, items } : item;
}

/** Narrow already-authorized navigation to the destinations in a custom workspace. */
export function filterSidebarNavByUrls(navData, pageUrls) {
  const selectedUrls = Array.isArray(pageUrls) ? pageUrls : [];
  return {
    ...navData,
    navMain: (navData.navMain || [])
      .map((item) => filterSidebarItemByUrls(item, selectedUrls))
      .filter(Boolean),
    navSecondary: (navData.navSecondary || [])
      .map((item) => filterSidebarItemByUrls(item, selectedUrls))
      .filter(Boolean),
  };
}

/** Keep authorized hub-search destinations explicitly selected for a custom workspace. */
export function filterSidebarPagesByUrls(pages, pageUrls) {
  const selectedUrls = new Set(Array.isArray(pageUrls) ? pageUrls : []);
  return (pages || []).filter((page) => selectedUrls.has(page.url));
}

/**
 * Keys to expand so the active leaf is visible (main + nested hub).
 * Nested children are matched before hub parents that share a path prefix.
 */
export function collectOpenMenuKeysForPath(navMain, currentPath) {
  const newOpenMenus = {};

  for (const mainItem of navMain || []) {
    if (!Array.isArray(mainItem.items)) continue;

    for (const subItem of mainItem.items) {
      const nestedItems = Array.isArray(subItem.items) ? subItem.items : [];
      const nestedMatch = nestedItems.some((child) => sidebarSubItemMatchesPath(currentPath, child));
      if (nestedMatch) {
        newOpenMenus[mainItem.id] = true;
        newOpenMenus[`${mainItem.id}-${subItem.id}`] = true;
        return newOpenMenus;
      }

      if (sidebarSubItemMatchesPath(currentPath, subItem)) {
        newOpenMenus[mainItem.id] = true;
        if (nestedItems.length > 0) {
          newOpenMenus[`${mainItem.id}-${subItem.id}`] = true;
        }
        return newOpenMenus;
      }
    }
  }

  return newOpenMenus;
}

export function mergeOpenMenus(currentMenus, menusToOpen) {
  const entries = Object.entries(menusToOpen);
  if (entries.length === 0) return currentMenus;

  const hasChanges = entries.some(([key, isOpen]) => currentMenus[key] !== isOpen);
  if (!hasChanges) return currentMenus;

  return { ...currentMenus, ...menusToOpen };
}

/** True when `item` or any nested nav child matches the current path. */
export function navBranchContainsPath(pathname, item) {
  if (sidebarSubItemMatchesPath(pathname, item)) return true;
  if (!Array.isArray(item.items)) return false;
  return item.items.some((child) => navBranchContainsPath(pathname, child));
}

/** Group filtered nav items by section for rendering. */
export function groupNavBySections(sections, navMain) {
  if (!Array.isArray(sections) || sections.length === 0) {
    return [{ id: "_all", label: null, items: navMain }];
  }

  return sections
    .map((section) => ({
      ...section,
      items: navMain.filter((item) => item.section === section.id),
    }))
    .filter((section) => section.items.length > 0);
}
